/**
 * Lead status chip — colour-coded `<Chip>` shared between the list table,
 * the mobile card-list view, and the detail drawer header (#21).
 *
 * Colour mapping (issue #21 hard constraint):
 *   - NEW       → primary (blue)
 *   - REVIEWED  → info (cyan)
 *   - CONTACTED → warning (amber)
 *   - QUALIFIED → success (green)
 *   - REJECTED  → default (grey)
 */

import type { ReactElement } from "react";
import { Chip } from "@mui/material";
import type { ChipProps } from "@mui/material";
import type { LeadStatus } from "@ottopilot/shared";

const STATUS_COLOR: Record<LeadStatus, ChipProps["color"]> = {
  NEW: "primary",
  REVIEWED: "info",
  CONTACTED: "warning",
  QUALIFIED: "success",
  REJECTED: "default",
};

export type LeadStatusChipProps = {
  status: LeadStatus;
  size?: ChipProps["size"];
};

export function LeadStatusChip({ status, size = "small" }: LeadStatusChipProps): ReactElement {
  return (
    <Chip
      size={size}
      label={status}
      color={STATUS_COLOR[status]}
      variant={status === "REJECTED" ? "outlined" : "filled"}
      data-testid={`lead-status-chip-${status}`}
    />
  );
}
